import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import LoadingScreen from '../components/LoadingScreen';

export default function CorporateEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', organizer: '', event_date: '', event_time_str: '', location: '', description: '', estimated_guests: '', tracks: '', sponsors: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      const res = await api.get('/corporate-events');
      setEvents(res.data);
    } catch {
      setMessage('Failed to load events');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/corporate-events', form);
      setMessage(`Event created! Public URL: ${res.data.share_url}`);
      setShowForm(false);
      setForm({ title: '', organizer: '', event_date: '', event_time_str: '', location: '', description: '', estimated_guests: '', tracks: '', sponsors: '' });
      loadEvents();
    } catch {
      setMessage('Failed to create event');
    } finally {
      setSubmitting(false);
    }
  }

  function copyLink(shareLink) {
    navigator.clipboard.writeText(`${window.location.origin}/corp-event/${shareLink}`);
    setMessage('Link copied!');
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Corporate Events</h1>
          <button onClick={() => setShowForm(!showForm)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            {showForm ? 'Cancel' : '+ New Event'}
          </button>
        </div>

        {message && <div className="mb-4 p-3 bg-blue-100 text-blue-800 rounded">{message}</div>}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Event Title *" className="border p-2 rounded w-full" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              <input placeholder="Organizer / Company" className="border p-2 rounded w-full" value={form.organizer} onChange={e => setForm({ ...form, organizer: e.target.value })} />
              <input required type="date" className="border p-2 rounded w-full" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} />
              <input placeholder="Time (e.g. 09:00)" className="border p-2 rounded w-full" value={form.event_time_str} onChange={e => setForm({ ...form, event_time_str: e.target.value })} />
              <input placeholder="Location / Venue" className="border p-2 rounded w-full" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
              <input type="number" placeholder="Estimated Guests" className="border p-2 rounded w-full" value={form.estimated_guests} onChange={e => setForm({ ...form, estimated_guests: e.target.value })} />
            </div>
            <textarea placeholder="Description" className="border p-2 rounded w-full" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <input placeholder="Tracks (comma-separated)" className="border p-2 rounded w-full" value={form.tracks} onChange={e => setForm({ ...form, tracks: e.target.value })} />
            <input placeholder="Sponsors (comma-separated)" className="border p-2 rounded w-full" value={form.sponsors} onChange={e => setForm({ ...form, sponsors: e.target.value })} />
            <button type="submit" disabled={submitting} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50">
              {submitting ? 'Creating...' : 'Create Event'}
            </button>
          </form>
        )}

        {events.length === 0 && !showForm && (
          <div className="text-center py-12 text-gray-500">No corporate events yet. Click "+ New Event" to create one.</div>
        )}

        <div className="space-y-4">
          {events.map(evt => (
            <div key={evt.id} className="bg-white p-5 rounded shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">{evt.title}</h3>
                  <p className="text-sm text-gray-600">{evt.organizer} — {evt.event_date} {evt.event_time_str}</p>
                  <p className="text-sm text-gray-500">{evt.location}</p>
                  <p className="text-sm mt-1">{evt.registered_count} registered / {evt.estimated_guests || '∞'} max</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyLink(evt.share_link)} className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200">Copy Link</button>
                  <button onClick={() => navigate(`/corp-event/${evt.share_link}`)} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200">Preview</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
